import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import { UPTIME_INCIDENT_TABLE_ANCHOR_ID } from "@/v2-backoffice-client/uptime/incidents/UptimeIncidentTable"
import {
  formatLatency,
  getCategoryLabel,
  getPlatformLabel,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  UPTIME_PLATFORM_DESCRIPTIONS,
  type UptimePlatform,
} from "@/v2-backoffice-client/uptime/uptime.constants"
import type {
  UptimeCategoryGroup,
  UptimeGroupStats,
  UptimePlatformGroup,
} from "@/v2-backoffice-client/uptime/uptime.buckets.utils"

export type UptimeCategoryNavProps = Omit<ComponentProps<"nav">, "onSelect"> & {
  // Ordered list of platforms with their per-category children, as
  // pre-computed by `buildUptimeBoardData`. The board's single fleet
  // pass already produced the per-category `UptimeGroupStats` we
  // need; here we only have to render.
  platforms: ReadonlyArray<UptimePlatformGroup>
  // Down/slow/drift counts across the post-mute fleet, used by the
  // pinned "Active incidents" pseudo-item.
  attentionCounts: Readonly<{ down: number; slow: number; drift: number }>
  // Currently selected entry id: UPTIME_INCIDENT_TABLE_ANCHOR_ID for
  // the incidents view, or a (platform, source) compound key for a
  // specific category.
  selected: string | null
  onSelect: (id: string) => void
}

// URL fragment-friendly anchor id for a category section. Used by
// UptimeCategoryTable to set the `id=` on its rendered <section> so
// the sidebar can scroll the right table into view.
export function categoryAnchorId(groupKey: string): string {
  return `uptime-cat-${groupKey.replace(/[^a-zA-Z0-9]+/g, "-")}`
}

function pickAttentionVariant(
  counts: UptimeCategoryNavProps["attentionCounts"],
): "destructive" | "warning" | "muted" {
  if (counts.down > 0) return "destructive"
  if (counts.slow > 0 || counts.drift > 0) return "warning"
  return "muted"
}

function pickAttentionIconClass(
  counts: UptimeCategoryNavProps["attentionCounts"],
): string {
  if (counts.down > 0) return "text-destructive"
  if (counts.slow > 0) return "text-amber-500"
  return "text-muted-foreground"
}

function pickGroupVariant(
  stats: UptimeGroupStats,
): "success-1" | "destructive" | "warning" {
  if (stats.down > 0) return "destructive"
  if (stats.slow > 0 || stats.skipped > 0 || stats.drift > 0) return "warning"
  return "success-1"
}

function UptimeCategoryNavCategoryRow(props: {
  group: UptimeCategoryGroup
  isActive: boolean
  onSelect: (id: string) => void
}) {
  const { group, isActive, onSelect } = props
  const stats = group.stats
  const variant = pickGroupVariant(stats)
  return (
    <button
      type="button"
      className={cn(
        "uptime-category-nav-item",
        isActive && "uptime-category-nav-item-active",
      )}
      onClick={() => onSelect(group.groupKey)}
    >
      <div className="uptime-category-nav-item-header">
        <Dot variant={variant} className="shrink-0" />
        <div className="uptime-category-nav-item-name">
          {getCategoryLabel(group.source)}
        </div>
        <div className="uptime-category-nav-item-count">
          {stats.up}
          <span className="text-muted-foreground">/{stats.total}</span>
        </div>
      </div>
      <div className="uptime-category-nav-item-stats">
        {stats.down > 0 && (
          <span className="text-destructive font-medium">
            {stats.down} down
          </span>
        )}
        {stats.slow > 0 && (
          <span className="text-amber-500 font-medium">{stats.slow} slow</span>
        )}
        {stats.skipped > 0 && (
          <span className="text-muted-foreground">{stats.skipped} skipped</span>
        )}
        {stats.drift > 0 && (
          <span className="text-violet-500 font-medium">
            {stats.drift} drift
          </span>
        )}
        {stats.down === 0 &&
          stats.slow === 0 &&
          stats.skipped === 0 &&
          stats.drift === 0 && (
            <span className="text-muted-foreground">all ok</span>
          )}
        {stats.muted > 0 && (
          <span className="text-muted-foreground/80">+{stats.muted} muted</span>
        )}
        {stats.medianLatencyMs != null && (
          <span className="text-muted-foreground ml-auto tabular-nums">
            {formatLatency(stats.medianLatencyMs)}
          </span>
        )}
      </div>
      {stats.aggregateUptimePercent != null && (
        <div className="uptime-category-nav-item-uptime">
          {stats.aggregateUptimePercent.toFixed(2)}% · 90 d
        </div>
      )}
    </button>
  )
}

function UptimeCategoryNavPlatformHeader(props: {
  platform: UptimePlatform
  stats: UptimeGroupStats
}) {
  const { platform, stats } = props
  const description = UPTIME_PLATFORM_DESCRIPTIONS[platform]
  return (
    <div className="uptime-category-nav-platform-header">
      <div className="uptime-category-nav-platform-name">
        {getPlatformLabel(platform)}
      </div>
      <div className="uptime-category-nav-platform-stats">
        <span className="font-medium tabular-nums">{stats.up}</span>
        <span className="text-muted-foreground">/{stats.total}</span>
        {stats.down > 0 && (
          <span className="text-destructive ml-1.5">· {stats.down} down</span>
        )}
        {stats.muted > 0 && (
          <span className="text-muted-foreground/80 ml-1.5">
            · +{stats.muted} muted
          </span>
        )}
      </div>
      {description && (
        <div className="uptime-category-nav-platform-desc">{description}</div>
      )}
    </div>
  )
}

function UptimeCategoryNav(props: UptimeCategoryNavProps) {
  const {
    platforms,
    attentionCounts,
    selected,
    onSelect,
    className,
    ...navProps
  } = props

  const attentionTotal =
    attentionCounts.down + attentionCounts.slow + attentionCounts.drift
  const hasAnyGroup = platforms.some((p) => p.groups.length > 0)
  if (!hasAnyGroup && attentionTotal === 0) return null

  const attentionActive = selected === UPTIME_INCIDENT_TABLE_ANCHOR_ID
  const attentionVariant = pickAttentionVariant(attentionCounts)
  const attentionIconClass = pickAttentionIconClass(attentionCounts)

  return (
    <nav
      className={cn("uptime-category-nav", className)}
      aria-label="Category navigation"
      {...navProps}
    >
      {/* Pinned "Active incidents" pseudo-item. Lives above the
          platform list so an operator landing on the page goes
          straight to the triage queue. */}
      <button
        type="button"
        className={cn(
          "uptime-category-nav-item",
          attentionActive && "uptime-category-nav-item-active",
        )}
        onClick={() => onSelect(UPTIME_INCIDENT_TABLE_ANCHOR_ID)}
      >
        <div className="uptime-category-nav-item-header">
          <Dot variant={attentionVariant} className="shrink-0" />
          <Icon
            src="triangle-warning"
            className={cn("text-sm shrink-0", attentionIconClass)}
          />
          <div className="uptime-category-nav-item-name">Active incidents</div>
          <div className="uptime-category-nav-item-count">{attentionTotal}</div>
        </div>
        <div className="uptime-category-nav-item-stats">
          {attentionCounts.down > 0 && (
            <span className="text-destructive font-medium">
              {attentionCounts.down} down
            </span>
          )}
          {attentionCounts.slow > 0 && (
            <span className="text-amber-500 font-medium">
              {attentionCounts.slow} slow
            </span>
          )}
          {attentionCounts.drift > 0 && (
            <span className="text-violet-500 font-medium">
              {attentionCounts.drift} drift
            </span>
          )}
          {attentionTotal === 0 && (
            <span className="text-muted-foreground">nothing to do</span>
          )}
        </div>
      </button>

      {hasAnyGroup && (
        <div className="uptime-category-nav-divider" role="presentation" />
      )}

      {platforms.map((p) => {
        if (p.groups.length === 0) return null
        return (
          <div key={p.platform} className="uptime-category-nav-platform">
            <UptimeCategoryNavPlatformHeader
              platform={p.platform}
              stats={p.stats}
            />
            {p.groups.map((g) => (
              <UptimeCategoryNavCategoryRow
                key={g.groupKey}
                group={g}
                isActive={selected === g.groupKey}
                onSelect={onSelect}
              />
            ))}
          </div>
        )
      })}
    </nav>
  )
}

export default UptimeCategoryNav
